import React, { FC } from 'react';
import styled from 'styled-components';

import DeleteButton from 'src/presentation/components/pages/shared/DeleteButton';
import { StyledTR, RowActionContainer } from 'src/presentation/components/pages/shared/Table';
import { DomainError } from 'src/models/DomainError';

import { ResetErrorFunction, TaggedNameValuePair } from 'src/presentation/components/pages/integrations/types';
import {
  MaskedField,
  WrappedField,
} from 'src/presentation/components/pages/integrations/editorComponents/EditorFields';

export type NameValueListRowProps = {
  item: TaggedNameValuePair;
  onChange: (item: TaggedNameValuePair) => void;
  onDelete: (item: TaggedNameValuePair) => void;
  masked: boolean;
  dataAid?: string;
  errors?: DomainError[];
  resetError?: ResetErrorFunction;
};

type EditableRowAttribute = 'name' | 'value';

const NameValueListRow: FC<NameValueListRowProps> = (props): JSX.Element => {
  const { item, dataAid, masked, errors, resetError, onChange, onDelete } = props;

  const handleFieldChange = (attr: EditableRowAttribute, value: string) => {
    const newValue = { ...item };
    newValue[attr] = value;
    resetError && resetError({ attr: `${item.id}.${attr}`, errors });
    onChange(newValue);
  };

  const deleteButton = item.isRequired ? (
    <DeleteIconPlaceholder />
  ) : (
    <RowActionButton
      id={item.id}
      onClick={() => {
        onDelete(item);
      }}
    />
  );

  return (
    <StyledTR data-aid={dataAid} inset="none">
      <CompactTD data-aid={`${dataAid}-${item.name}`}>
        <WrappedField
          attr={`${item.id}.name`}
          data-aid={`${dataAid}-${item.name}-name`}
          value={item.name}
          readOnly={!!item.isRequired}
          stateless={true}
          onChange={value => {
            handleFieldChange('name', value);
          }}
        />
      </CompactTD>
      <CompactTD>
        <MaskedField
          attr={`${item.id}.value`}
          data-aid={`${dataAid}-${item.name}-value`}
          isMasked={masked}
          value={item.value}
          onChange={value => {
            handleFieldChange('value', value);
          }}
        />
      </CompactTD>
      <CompactActionTD style={{ width: '1%' }}>
        <RowActionContainer>{deleteButton}</RowActionContainer>
      </CompactActionTD>
    </StyledTR>
  );
};

const CompactTD = styled.td`
  padding: 4px;
  vertical-align: top;
`;

const CompactActionTD = styled.td`
  padding: 4px 12px 4px 0;
`;

const DeleteIconPlaceholder = styled.div`
  width: 26px;
`;

const RowActionButton = styled(DeleteButton)`
  visibility: visible;
`;

export default NameValueListRow;
